import { Badge, Skeleton } from '@/components/ui';
import { Textarea } from '@/components/ui/textarea';
import type {
  HumanReviewDecision,
  HumanReviewDecisionResult,
  HumanReviewRequest,
} from '@/lib/admin/client';
import { humanReviewDecisionActor } from '@/lib/admin/client';

import {
  decisionResultBadgeVariant,
  humanReviewStateBadgeVariant,
} from './badgeVariants';
import { DecisionButtons } from './DecisionButtons';

export type ReviewDecisionHistoryProps = {
  item: HumanReviewRequest;
  decisions: HumanReviewDecision[];
  isLoading: boolean;
  isError: boolean;
  reason: string;
  onReasonChange: (reason: string) => void;
  onDecide: (action: HumanReviewDecisionResult) => void;
  isDeciding: boolean;
};

export function HumanReviewDecisionHistory({
  item,
  decisions,
  isLoading,
  isError,
  reason,
  onReasonChange,
  onDecide,
  isDeciding,
}: ReviewDecisionHistoryProps) {
  const itemIsExpired = item.state === 'expired';

  return (
    <section className="border-border rounded-md border p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-base font-semibold">Decision history</h2>
          <p className="text-muted-foreground mt-1 text-sm">
            Current sequence {item.latestSequence}
          </p>
        </div>
        {item.latestDecision && (
          <Badge variant={humanReviewStateBadgeVariant(item.state)}>
            Latest: {item.latestDecision.result.replace('_', ' ')}
          </Badge>
        )}
      </div>
      {isLoading && <Skeleton className="mt-4 h-16 w-full" />}
      {isError && (
        <p className="text-destructive mt-4 text-sm">
          Decision history could not be loaded.
        </p>
      )}
      {!isLoading && !isError && decisions.length === 0 && (
        <p className="text-muted-foreground mt-4 text-sm">
          No decisions recorded yet.
        </p>
      )}
      {decisions.length > 0 && (
        <ol className="mt-4 space-y-3">
          {decisions.map((decision) => (
            <li
              key={decision.id}
              className="border-border rounded-md border p-3 text-sm"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Badge variant={decisionResultBadgeVariant(decision.result)}>
                  #{decision.sequence} {decision.result.replace('_', ' ')}
                </Badge>
                <time className="text-muted-foreground text-xs">
                  {new Date(decision.decidedAt).toLocaleString()}
                </time>
              </div>
              <p className="text-muted-foreground mt-2">
                {humanReviewDecisionActor(decision)}
                {decision.reason ? `: ${decision.reason}` : ''}
              </p>
            </li>
          ))}
        </ol>
      )}
      <div className="border-border mt-4 space-y-3 border-t pt-4">
        <Textarea
          value={reason}
          onChange={(event) => onReasonChange(event.target.value)}
          placeholder={itemIsExpired ? 'Reopen note' : 'Correction note'}
          aria-label="Correction note"
        />
        <div className="flex flex-wrap justify-end gap-2">
          <DecisionButtons
            onDecide={onDecide}
            disabled={isDeciding}
            state={item.state}
            correction={item.decisionCount > 0}
          />
        </div>
      </div>
    </section>
  );
}
